import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Database,
  RefreshCw,
  Save,
  Trash2,
  Users,
  Building2,
  FileText,
  Briefcase,
  AlertTriangle,
  CheckCircle2,
  Info,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { RichDemoDataCard } from './RichDemoDataCard';
import { Input } from './ui/input';

interface DemoStatus {
  hasBaseline: boolean;
  baselineCreatedAt: string | null;
  currentData: {
    companies: number;
    users: number;
    consultingFirms: number;
    systemTemplates: number;
    customTemplates: number;
    totalSOWs: number;
  };
}

interface DemoManagementProps {
  accessToken: string;
}

export function DemoManagement({ accessToken }: DemoManagementProps) {
  // Rich demo data targets one firm; Solo Consultant Pro is the firm the product audit walks use.
  const [richSeedFirmId, setRichSeedFirmId] = useState('demo-firm-solo');
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [showCreateBaselineDialog, setShowCreateBaselineDialog] = useState(false);
  const [showClearAndSeedDialog, setShowClearAndSeedDialog] = useState(false);
  const [demoCredentials, setDemoCredentials] = useState<any[]>([]);
  const [showDemoUsersDialog, setShowDemoUsersDialog] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/demo/status`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch demo status');
      }

      const data = await response.json();
      setStatus(data.status);
    } catch (error) {
      console.error('Error fetching demo status:', error);
      toast.error('Failed to load demo status');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBaseline = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/demo/create-baseline`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create baseline');
      }

      const data = await response.json();
      toast.success('Demo baseline created successfully');
      setShowCreateBaselineDialog(false);
      fetchStatus();
    } catch (error) {
      console.error('Error creating baseline:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create baseline');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/demo/reset`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reset demo');
      }

      const data = await response.json();
      toast.success('Demo environment reset successfully');
      setShowResetDialog(false);
      fetchStatus();
    } catch (error) {
      console.error('Error resetting demo:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to reset demo');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/demo/seed`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to seed demo data');
      }

      const data = await response.json();
      toast.success('Demo data seeded successfully');
      setShowSeedDialog(false);
      fetchStatus();
    } catch (error) {
      console.error('Error seeding demo data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to seed demo data');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndSeed = async () => {
    try {
      setLoading(true);
      toast.info('Starting clear and seed operation... You can navigate away from this page - the process will continue running on the server.', {
        duration: 10000, // Show for 10 seconds (operation will complete and show success/error after)
      });

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/system-admin/demo/clear-and-seed`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken || publicAnonKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to clear and seed demo data');
      }

      const data = await response.json();
      toast.success('Demo data cleared and seeded successfully. All 4 demo firm subdomains are live.', {
        duration: 5000,
      });
      setShowClearAndSeedDialog(false);
      fetchStatus();
    } catch (error) {
      console.error('Error clearing and seeding demo data:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to clear and seed demo data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDemoUsers = async () => {
    try {
      setLoading(true);
      
      console.log('[DEMO] Creating demo users...');
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/setup-demo-users`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
          },
        }
      );

      console.log('[DEMO] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DEMO] Error response:', errorText);
        let error;
        try {
          error = JSON.parse(errorText);
        } catch (e) {
          throw new Error(errorText || 'Failed to create demo users');
        }
        throw new Error(error.error || 'Failed to create demo users');
      }

      const data = await response.json();
      console.log('[DEMO] Demo users created:', data);
      
      setDemoCredentials(data.users);
      setShowDemoUsersDialog(true);
      toast.success(`Created ${data.users.length} demo users successfully`);
      fetchStatus(); // Refresh to show new users
      
      // Dispatch event to notify other components (like All Users tab) to refresh
      window.dispatchEvent(new CustomEvent('pathkit-demo-users-created'));
    } catch (error) {
      console.error('Error creating demo users:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create demo users');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDemoEnvironment = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/setup-demo-environment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken || publicAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ force: true }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to restore demo environment');
      }

      const data = await response.json();
      setDemoCredentials(data.users);
      setShowDemoUsersDialog(true);
      toast.success('Demo environment restored successfully');
      fetchStatus(); // Refresh to show new data
      
      // Dispatch event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('pathkit-demo-users-created'));
    } catch (error) {
      console.error('Error restoring demo environment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to restore demo environment');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2">
          <Database className="w-5 h-5" />
          Demo Environment Management
        </h2>
        <p className="text-muted-foreground">
          Manage demo data, create baselines, and reset to clean state
        </p>
      </div>

      {/* Baseline Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Save className="w-4 h-4" />
            Demo Baseline
          </CardTitle>
          <CardDescription>
            Snapshot of the demo environment that can be restored
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              {status?.hasBaseline ? (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Baseline exists</span>
                  </div>
                  {status.baselineCreatedAt && (
                    <p className="text-sm text-muted-foreground">
                      Created: {new Date(status.baselineCreatedAt).toLocaleString()}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  <span>No baseline created</span>
                </div>
              )}
            </div>
            <Button
              onClick={() => setShowCreateBaselineDialog(true)}
              disabled={loading}
              variant={status?.hasBaseline ? "outline" : "default"}
            >
              <Save className="w-4 h-4 mr-2" />
              {status?.hasBaseline ? 'Update Baseline' : 'Create Baseline'}
            </Button>
          </div>

          <div className="rounded-lg border p-4 bg-muted/50">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-blue-600" />
              <div className="text-sm">
                <p className="mb-2">The baseline is a snapshot of your current demo environment.</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>System templates are always preserved (never reset)</li>
                  <li>System admin accounts are always preserved</li>
                  <li>All other data can be reset to this baseline</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rich demo data for one firm: every engagement stage, populated grids, messaging, change orders */}
      <div className="space-y-2">
        <label className="text-sm text-muted-foreground" htmlFor="rich-seed-firm">Firm to seed (consulting firm id)</label>
        <Input id="rich-seed-firm" value={richSeedFirmId} onChange={(e) => setRichSeedFirmId(e.target.value)} className="max-w-sm" />
      </div>
      <RichDemoDataCard accessToken={accessToken} firmId={richSeedFirmId.trim() || undefined} onComplete={fetchStatus} />

      {/* Current Data Status */}
      <Card>
        <CardHeader>
          <CardTitle>Current Environment Data</CardTitle>
          <CardDescription>
            Overview of data in the current demo environment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <Briefcase className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Consulting Firms</p>
                  <p className="text-2xl">{status.currentData.consultingFirms}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <Building2 className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Companies</p>
                  <p className="text-2xl">{status.currentData.companies}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <Users className="w-8 h-8 text-purple-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Users</p>
                  <p className="text-2xl">{status.currentData.users}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <FileText className="w-8 h-8 text-orange-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Total SOWs</p>
                  <p className="text-2xl">{status.currentData.totalSOWs}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <Database className="w-8 h-8 text-indigo-600" />
                <div>
                  <p className="text-sm text-muted-foreground">System Templates</p>
                  <p className="text-2xl">{status.currentData.systemTemplates}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg border">
                <Database className="w-8 h-8 text-pink-600" />
                <div>
                  <p className="text-sm text-muted-foreground">Custom Templates</p>
                  <p className="text-2xl">{status.currentData.customTemplates}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Demo Actions</CardTitle>
          <CardDescription>
            Manage your demo environment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Seed Demo Data */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4" />
                <span>Seed Demo Data</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Add realistic demo data: 3 firms, 10 companies, users, and SOWs
              </p>
            </div>
            <Button
              onClick={() => setShowSeedDialog(true)}
              disabled={loading}
              variant="outline"
            >
              <Database className="w-4 h-4 mr-2" />
              Seed Data
            </Button>
          </div>

          <Separator />

          {/* Reset Demo */}
          <div className="flex items-center justify-between p-4 border rounded-lg border-orange-200 bg-orange-50/50">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-orange-600" />
                <span>Reset to Baseline</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Delete all current data and restore from baseline (preserves system templates & admins)
              </p>
            </div>
            <Button
              onClick={() => setShowResetDialog(true)}
              disabled={loading || !status?.hasBaseline}
              variant="outline"
              className="border-orange-600 text-orange-600 hover:bg-orange-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset Demo
            </Button>
          </div>

          <Separator />

          {/* Clear and Seed Demo */}
          <div className="flex items-center justify-between p-4 border rounded-lg border-red-200 bg-red-50/50">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-red-600" />
                <span>Clear and Seed Demo Data</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Delete all current data and seed with realistic demo data
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Password for all demo users: <code className="bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono">Demo2025!</code>
              </p>
            </div>
            <Button
              onClick={() => setShowClearAndSeedDialog(true)}
              disabled={loading}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear and Seed
            </Button>
          </div>

          <Separator />

          {/* Create Demo Users */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Create Demo Users</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Add a set of demo users to your environment
              </p>
            </div>
            <Button
              onClick={handleCreateDemoUsers}
              disabled={loading}
              variant="outline"
            >
              <Users className="w-4 h-4 mr-2" />
              Create Users
            </Button>
          </div>

          <Separator />

          {/* Restore Demo Environment */}
          <div className="flex items-center justify-between p-4 border rounded-lg border-blue-200 bg-blue-50/50">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span>Restore Demo Environment</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Restore staging.pathkit.pro with Demo Company and demo users
              </p>
            </div>
            <Button
              onClick={handleRestoreDemoEnvironment}
              disabled={loading}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Restore Demo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create Baseline Dialog */}
      <AlertDialog open={showCreateBaselineDialog} onOpenChange={setShowCreateBaselineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {status?.hasBaseline ? 'Update Demo Baseline?' : 'Create Demo Baseline?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will create a snapshot of your current environment including:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>All consulting firms</li>
                <li>All companies and their data</li>
                <li>All users (except system admins)</li>
                <li>All SOWs and configuration items</li>
              </ul>
              <p className="mt-2">
                {status?.hasBaseline 
                  ? 'This will replace the existing baseline.' 
                  : 'You can restore to this baseline later.'}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateBaseline}
              disabled={loading}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Create Baseline
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Reset Demo Environment?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Warning:</strong> This will delete all current data and restore from the baseline.
              <div className="mt-3 space-y-2">
                <p><strong>Preserved:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>System templates (Atlassian, ServiceNow, etc.)</li>
                  <li>System administrator accounts</li>
                </ul>
                <p className="mt-2"><strong>Deleted and Restored:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>All consulting firms</li>
                  <li>All companies and SOWs</li>
                  <li>All non-admin users</li>
                  <li>All configuration items</li>
                </ul>
              </div>
              <p className="mt-3 text-red-600">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset Demo
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seed Dialog */}
      <AlertDialog open={showSeedDialog} onOpenChange={setShowSeedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Seed Demo Data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will add realistic demo data to your environment:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>3 consulting firms (TechConsult, CloudPros, Agile Advisors)</li>
                <li>10 client companies across various industries</li>
                <li>Users for each company</li>
                <li>5 sample SOWs with different templates</li>
              </ul>
              <p className="mt-2">
                This data will be added to your existing environment.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSeed}
              disabled={loading}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Seed Data
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear and Seed Dialog */}
      <AlertDialog open={showClearAndSeedDialog} onOpenChange={setShowClearAndSeedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clear and Seed Demo Data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>Warning:</strong> This will delete all current data and seed with realistic demo data.
              <div className="mt-3 space-y-2">
                <p><strong>Preserved:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>System templates (Atlassian, ServiceNow, etc.)</li>
                  <li>System administrator accounts</li>
                </ul>
                <p className="mt-2"><strong>Deleted and Seeded:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>All consulting firms</li>
                  <li>All companies and SOWs</li>
                  <li>All non-admin users</li>
                  <li>All configuration items</li>
                </ul>
              </div>
              <p className="mt-3 text-red-600">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAndSeed}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clearing and Seeding...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear and Seed
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Demo Users Dialog */}
      <AlertDialog open={showDemoUsersDialog} onOpenChange={setShowDemoUsersDialog}>
        <AlertDialogContent className="max-w-6xl w-[95vw] max-h-[85vh] flex flex-col">
          <AlertDialogHeader className="flex-shrink-0">
            <AlertDialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Demo Users Created
            </AlertDialogTitle>
            <AlertDialogDescription>
              The following demo users have been created. All users share the same password for testing convenience.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="rounded-lg border">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="min-w-[220px]">Email</TableHead>
                      <TableHead className="min-w-[100px]">Password</TableHead>
                      <TableHead className="min-w-[120px]">Role</TableHead>
                      <TableHead className="min-w-[140px]">Team</TableHead>
                      <TableHead className="min-w-[120px]">Company</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoCredentials.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-sm">{user.email}</TableCell>
                        <TableCell className="font-mono text-sm">{user.password}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'company_admin' ? 'default' : 'secondary'}>
                            {user.role === 'company_admin' ? 'Client Engagement Lead' : 'Client Team Member'}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.team}</TableCell>
                        <TableCell className="font-medium">{user.company}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              navigator.clipboard.writeText(`${user.email}\n${user.password}`);
                              toast.success('Credentials copied to clipboard');
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            
            <div className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />
                <div className="text-sm flex-1">
                  <p className="mb-2"><strong>How to use these accounts:</strong></p>
                  <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
                    <li>Go to the login page at demo.pathkit.pro</li>
                    <li>Enter any of the emails above with password: <code className="bg-white dark:bg-gray-800 px-2 py-0.5 rounded font-mono text-xs">DemoPass!</code></li>
                    <li>Explore the application from that user's perspective</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
          
          <AlertDialogFooter className="flex-shrink-0">
            <Button onClick={() => setShowDemoUsersDialog(false)}>Close</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}